import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DiffResult, ProductChange, deleteKey } from '../../utils/service-provider-xlsx';

export interface ImportPreviewResult {
  confirmed: boolean;
  // Keys of deletes the user confirmed, via deleteKey(serviceId, productId).
  confirmedDeleteKeys: Set<string>;
}

interface DeleteRow {
  change: ProductChange;
  selected: boolean;
}

interface FieldChange {
  label: string;
  before: string;
  after: string;
}

// Product fields shown in the preview, in display order.
const PRODUCT_FIELDS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'cost', label: 'Cost' },
  { key: 'bespoke', label: 'Bespoke' },
  { key: 'invoiceLabel', label: 'Invoice Label' },
  { key: 'description', label: 'Description' },
  { key: 'note', label: 'Note' },
];

@Component({
  selector: 'app-service-provider-import-preview',
  templateUrl: './service-provider-import-preview.component.html',
  styleUrls: ['./service-provider-import-preview.component.scss'],
})
export class ServiceProviderImportPreviewComponent {
  diff: DiffResult;
  deleteRows: DeleteRow[];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {
    this.diff = this.config.data.diff as DiffResult;
    // Delete toggles default OFF — an accidental Delete?=Yes won't delete
    // anything unless the user actively flips the switch here.
    this.deleteRows = this.diff.deleted.map((change) => ({ change, selected: false }));
  }

  get hasErrors(): boolean {
    return this.diff.errors.length > 0;
  }

  get hasChanges(): boolean {
    return (
      this.diff.business.length > 0 ||
      this.diff.created.length > 0 ||
      this.diff.updated.length > 0 ||
      this.deleteRows.length > 0
    );
  }

  get selectedDeleteCount(): number {
    return this.deleteRows.filter((r) => r.selected).length;
  }

  // For an update, the per-field before -> after of fields that actually
  // changed, so the user sees exactly what's being modified.
  fieldChanges(change: ProductChange): FieldChange[] {
    const previous: any = change.previous ?? {};
    const next: any = change.row;
    const result: FieldChange[] = [];

    for (const field of PRODUCT_FIELDS) {
      const before = this.formatValue(field.key, previous[field.key]);
      const after = this.formatValue(field.key, next[field.key]);
      if (before !== after) {
        result.push({ label: field.label, before, after });
      }
    }

    return result;
  }

  // A readable, single-line summary of every value on a product (used for the
  // new-items and to-delete tables, which show the full row rather than a diff).
  formatValue(key: string, value: any): string {
    if (key === 'bespoke') {
      return value ? 'Yes' : 'No';
    }
    if (value === null || value === undefined || value === '') {
      return '—';
    }
    return String(value);
  }

  onCancel(): void {
    this.ref.close({ confirmed: false, confirmedDeleteKeys: new Set<string>() } as ImportPreviewResult);
  }

  onApply(): void {
    const confirmedDeleteKeys = new Set<string>(
      this.deleteRows
        .filter((r) => r.selected)
        .map((r) => deleteKey(r.change.serviceId, r.change.row.id))
    );
    this.ref.close({ confirmed: true, confirmedDeleteKeys } as ImportPreviewResult);
  }
}
